import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { ShortcutCheatsheet } from './ShortcutCheatsheet';

// Colors mirror the CSS applied by CustomDataTable / index.css
const LEGEND_ITEMS = [
  {
    label: 'Editable',
    color: '#FFFBE5',
    border: '1px solid #d4b800',
    textBold: false,
    tooltip: 'You can type values into these cells',
  },
  {
    label: 'Read-only',
    color: '#FFFFFF',
    border: '1px solid #d0d0d0',
    textBold: false,
    tooltip: 'These cells are locked and cannot be edited',
  },
  {
    label: 'Calculated',
    color: '#FFFFFF',
    border: '1px solid #d0d0d0',
    textBold: true,
    tooltip: 'Values computed by the cube formula engine',
  },
  {
    label: 'Modified',
    color: '#f2b7b7',
    border: '1px solid #d88080',
    textBold: false,
    tooltip: 'Cells with unsaved changes',
  },
];

interface CubeEntryLegendProps {
  onOpenDeveloperTools?: () => void;
  onOpenReferenceAudit?: () => void;
}

export function CubeEntryLegend({ onOpenDeveloperTools, onOpenReferenceAudit }: CubeEntryLegendProps = {}) {
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);

  return (
    <>
      <Stack
        direction='row'
        alignItems='center'
        sx={{
          px: 2,
          py: 0.75,
          bgcolor: '#fafafa',
          borderBottom: '1px solid #E3EBF6',
        }}
      >
        <Typography variant='caption' sx={{ color: '#888', fontWeight: 600, whiteSpace: 'nowrap', mr: 2 }}>
          Legend:
        </Typography>

        <Stack direction='row' spacing={2.5} alignItems='center' flex={1}>
          {LEGEND_ITEMS.map((item) => (
            <Tooltip key={item.label} title={item.tooltip} arrow placement='top'>
              <Stack direction='row' spacing={0.75} alignItems='center' sx={{ cursor: 'default' }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    bgcolor: item.color,
                    border: item.border,
                    borderRadius: '2px',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant='caption'
                  sx={{ color: '#555', fontWeight: item.textBold ? 700 : 400, whiteSpace: 'nowrap' }}
                >
                  {item.label}
                </Typography>
              </Stack>
            </Tooltip>
          ))}
        </Stack>

        {onOpenDeveloperTools && (
          <Tooltip title='Developer Tools' arrow placement='top'>
            <IconButton
              size='small'
              onClick={onOpenDeveloperTools}
              sx={{ color: '#aaa', '&:hover': { color: '#3B4797' } }}
            >
              <BuildOutlinedIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        )}

        {onOpenReferenceAudit && (
          <Tooltip title='Reference Audit' arrow placement='top'>
            <IconButton
              size='small'
              onClick={onOpenReferenceAudit}
              sx={{ color: '#aaa', '&:hover': { color: '#3B4797' } }}
            >
              <FactCheckOutlinedIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title='Keyboard shortcuts' arrow placement='top'>
          <IconButton size='small' onClick={() => setCheatsheetOpen(true)} sx={{ color: '#aaa', '&:hover': { color: '#42BB93' } }}>
            <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <ShortcutCheatsheet open={cheatsheetOpen} onClose={() => setCheatsheetOpen(false)} />
    </>
  );
}
